// Explore › Traces' query bar (APS-198, part C): a few words turned into the structured query
// the API takes (POST /explore/traces). The API builds the SQL; this only reads the words.
//
//   app.start  /  "GET /orders/{id}"   the span's name (any bare words, or span.name:...)
//   http.request.method:POST           a field or attribute equals a value (quotes for spaces)
//   -os.name:Android                   ... does not equal it
//   span.kind:CLIENT span.status_code:ERROR   a kind, a status (the API takes any case)
//   has:error.type / -has:error.type   the attribute is there, or is not
//   is:failed  is:root                 failed spans only; spans without a parent only
//   duration>=250ms  duration<=2s      at least, at most this long (ms or s; > and < too)
//
// Anything else is an error with its position, and nothing is sent.
import {
  FIELD,
  type LogFilter,
  type LogQueryError,
  MAX_FILTERS,
  MAX_VALUE,
  tokenize,
} from './logQuery'

export interface SpanQuery {
  filters: LogFilter[]
  failed?: true
  root?: true
  minMs?: number
  maxMs?: number
}

export type SpanQueryError =
  | LogQueryError
  | { code: 'duration'; position: number; value: string }
  | { code: 'is'; position: number; value: string }

/** The values span.kind and span.status_code take (APS-331), as the API names them. */
export const SPAN_KINDS = ['INTERNAL', 'SERVER', 'CLIENT', 'PRODUCER', 'CONSUMER'] as const
export const SPAN_STATUSES = ['UNSET', 'OK', 'ERROR'] as const

/** The API's bound on a duration: a day. */
const MAX_DURATION_MS = 86_400_000
const DURATION = /^duration(>=|<=|>|<)(\d+(?:\.\d+)?)(ms|s)$/i

/** Reads the query bar's words; an error says what and where. */
export function parseSpanQuery(input: string): { query: SpanQuery } | { error: SpanQueryError } {
  const tokens = tokenize(input)
  if (!Array.isArray(tokens)) return { error: tokens }
  const query: SpanQuery = { filters: [] }
  const words: string[] = []
  for (const token of tokens) {
    if (!token.phrase && /^duration[<>]/i.test(token.text)) {
      const m = DURATION.exec(token.text)
      const ms = m ? Number(m[2]) * ((m[3] as string).toLowerCase() === 's' ? 1000 : 1) : NaN
      if (!m || !(ms <= MAX_DURATION_MS))
        return { error: { code: 'duration', position: token.position, value: token.text } }
      if ((m[1] as string).startsWith('>')) query.minMs = ms
      else query.maxMs = ms
      continue
    }
    if (token.phrase || token.colon < 0) {
      words.push(token.text)
      continue
    }
    const negated = token.text.startsWith('-')
    const key = token.text.slice(negated ? 1 : 0, token.colon)
    const value = token.text.slice(token.colon + 1)
    if (key === 'is' && !negated) {
      if (value === 'failed') query.failed = true
      else if (value === 'root') query.root = true
      else return { error: { code: 'is', position: token.position, value } }
      continue
    }
    if (key === 'has') {
      if (!FIELD.test(value)) return { error: { code: 'field', position: token.position, value } }
      query.filters.push({ field: value, op: negated ? 'not_exists' : 'exists' })
      continue
    }
    if (!FIELD.test(key)) return { error: { code: 'field', position: token.position, value: key } }
    if (!value) return { error: { code: 'empty', position: token.position, value: key } }
    if (value.length > MAX_VALUE) return { error: { code: 'tooLong', position: token.position } }
    query.filters.push({ field: key, op: negated ? 'neq' : 'eq', value })
  }
  const name = words.join(' ').trim()
  if (name.length > MAX_VALUE) return { error: { code: 'tooLong', position: 1 } }
  if (name) query.filters.unshift({ field: 'span.name', op: 'eq', value: name })
  if (query.filters.length > MAX_FILTERS)
    return { error: { code: 'tooMany', position: tokens.at(-1)?.position ?? 1 } }
  if (query.minMs !== undefined && query.maxMs !== undefined && query.minMs > query.maxMs)
    return { error: { code: 'duration', position: 1, value: `${query.minMs} > ${query.maxMs}` } }
  return { query }
}

const quote = (v: string) => (/[\s"]/.test(v) || v === '' ? `"${v.replace(/"/g, '')}"` : v)

/** The query bar's words for a query: what a click on a suggestion writes back. */
export function formatSpanQuery(query: SpanQuery): string {
  const parts: string[] = []
  for (const f of query.filters) {
    if (f.op === 'exists') parts.push(`has:${f.field}`)
    else if (f.op === 'not_exists') parts.push(`-has:${f.field}`)
    else parts.push(`${f.op === 'neq' ? '-' : ''}${f.field}:${quote(f.value ?? '')}`)
  }
  if (query.failed) parts.push('is:failed')
  if (query.root) parts.push('is:root')
  if (query.minMs !== undefined) parts.push(`duration>=${query.minMs}ms`)
  if (query.maxMs !== undefined) parts.push(`duration<=${query.maxMs}ms`)
  return parts.join(' ')
}
